import { useMemo } from 'react'

import { demoProducts } from '../../demo/store'
import type { CreativeFilter, CreativeKind, CreativeLibraryItem, CreativeSource } from '../../demo/types'
import { Card, Icon } from '../../ui'
import { creativeCopy } from './copy'
import styles from './CreativesPage.module.css'

type Usage = NonNullable<CreativeFilter['usage']>

interface FilterRailProps {
  creatives: CreativeLibraryItem[]
  filter: CreativeFilter
  source: CreativeSource | 'all'
  kind: CreativeKind | 'all'
  usage: Usage
  productId?: string
  onSource: (value: CreativeSource | 'all') => void
  onKind: (value: CreativeKind | 'all') => void
  onUsage: (value: Usage) => void
  onProduct: (value?: string) => void
}

function matches(item: CreativeLibraryItem, filter: CreativeFilter, omit?: keyof CreativeFilter) {
  if (omit !== 'source' && filter.source && filter.source !== 'all' && item.source !== filter.source) return false
  if (omit !== 'kind' && filter.kind && filter.kind !== 'all' && item.kind !== filter.kind) return false
  if (omit !== 'productId' && filter.productId && item.productId !== filter.productId) return false
  if (omit !== 'usage' && filter.usage && filter.usage !== 'all' && (filter.usage === 'used') !== (item.usedByAds > 0)) return false
  if (omit !== 'q' && filter.q) {
    const product = demoProducts.find((candidate) => candidate.id === item.productId)?.name ?? ''
    const haystack = `${item.name} ${item.kol ?? ''} ${product}`.toLowerCase()
    if (!haystack.includes(filter.q.toLowerCase())) return false
  }
  return true
}

function Option({ label, active, count, onClick, title }: { label: string; active: boolean; count?: number; onClick: () => void; title?: string }) {
  return (
    <button type="button" aria-pressed={active} className={`${styles.option} ${active ? styles.optionOn : ''}`} onClick={onClick} title={title}>
      <span className={styles.box}>{active ? <Icon name="check" size={12} strokeWidth={3} /> : null}</span><span>{label}</span>{count === undefined ? null : <span className={styles.optionCount}>{count}</span>}
    </button>
  )
}

export function FilterRail(props: FilterRailProps) {
  const productIds = useMemo(() => {
    const seen = new Set<string>()
    return [...props.creatives].sort((a, b) => a.id.localeCompare(b.id)).flatMap((item) => {
      if (seen.has(item.productId)) return []
      seen.add(item.productId)
      return [item.productId]
    })
  }, [props.creatives])
  const facet = (key: keyof CreativeFilter, value: string) => props.creatives.filter((item) => matches(item, { ...props.filter, [key]: value }, key)).filter((item) => {
    if (key === 'source') return item.source === value
    if (key === 'kind') return item.kind === value
    if (key === 'usage') return (value === 'used') === (item.usedByAds > 0)
    return item.productId === value
  }).length

  return (
    <aside data-scope="creatives-filter">
      <Card className={styles.rail}>
        <section className={styles.group}><h5>{creativeCopy.filters.source}</h5>
          <Option label="全部" active={props.source === 'all'} onClick={() => props.onSource('all')} />
          {(['ai', 'upload', 'kol'] as const).map((value) => <Option key={value} label={creativeCopy.source[value]} active={props.source === value} count={facet('source', value)} onClick={() => props.onSource(value)} />)}
        </section>
        <section className={styles.group}><h5>{creativeCopy.filters.kind}</h5>
          <Option label="全部" active={props.kind === 'all'} onClick={() => props.onKind('all')} />
          {(['video', 'image'] as const).map((value) => <Option key={value} label={creativeCopy.kind[value]} active={props.kind === value} count={facet('kind', value)} onClick={() => props.onKind(value)} />)}
        </section>
        <section className={styles.group}><h5>{creativeCopy.filters.product}</h5>
          <Option label={creativeCopy.allProducts} active={!props.productId} onClick={() => props.onProduct(undefined)} />
          {productIds.map((id) => <Option key={id} label={demoProducts.find((product) => product.id === id)?.name ?? id} active={props.productId === id} count={facet('productId', id)} onClick={() => props.onProduct(id)} />)}
        </section>
        <section className={`${styles.group} ${styles.lastGroup}`}><h5>{creativeCopy.filters.usage}</h5>
          <Option label="全部" active={props.usage === 'all'} onClick={() => props.onUsage('all')} />
          {(['used', 'unused'] as const).map((value) => <Option key={value} label={creativeCopy.usage[value]} active={props.usage === value} count={facet('usage', value)} onClick={() => props.onUsage(value)} />)}
        </section>
      </Card>
    </aside>
  )
}
